import classNames from 'clsx';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { sum } from 'ramda';
import React, { PureComponent } from 'react';

import styles from './Carousel.css';

// Карточки тарифов расположены лесенкой в слайдере
const stairKoeffs = [263, 320, 370];
const isStairMod = (modificator) => modificator === 'stairs';

export default class Carousel extends PureComponent {
    static propTypes = {
        list: PropTypes.array.isRequired,
        breakpoints: PropTypes.array,
        showCount: PropTypes.number,
        fixedItemWidth: PropTypes.number,
        renderItem: PropTypes.func.isRequired,
        step: PropTypes.number,
        outerControls: PropTypes.bool,
        centerControls: PropTypes.bool,
        fixedTop: PropTypes.bool,
        controlsColor: PropTypes.oneOf(['primary', 'default']),
        animateItems: PropTypes.bool,
        setWidthOnResize: PropTypes.bool,
        overflow: PropTypes.bool,
        startPosition: PropTypes.number,
        focusItemWidthKoeff: PropTypes.number,
        modificator: PropTypes.oneOf(['', 'stairs', 'arrow', 'pointer']),
        setCarouselLoaded: PropTypes.func,
    };

    static defaultProps = {
        showCount: 3,
        startPosition: 0,
        controlsColor: 'default',
        step: 1,
        animateItems: true,
        setWidthOnResize: true,
        fixedTop: false,
        centerControls: false,
        focusItemWidthKoeff: 1,
        modificator: '',
        fixedItemWidth: 0,
    };

    state = {
        position: this.props.startPosition,
        showCount: this.props.showCount,
        selectedIdx: 1,
        itemWidths: [],
        list: [],
    };

    componentDidMount() {
        this.setWidth();

        if (this.props.setWidthOnResize) {
            window.addEventListener('resize', this.setWidthDebounced);
        }

        if (this.props.setCarouselLoaded) {
            this.props.setCarouselLoaded(true);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.breakpoints !== this.props.breakpoints) {
            this.setWidth();
        }
    }

    componentWillUnmount() {
        if (this.props.setWidthOnResize) {
            window.removeEventListener('resize', this.setWidthDebounced);
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (prevState.list.length !== nextProps.list.length) {
            return {
                list: nextProps.list,
                position: nextProps.startPosition,
                selectedIdx: nextProps.startPosition + 1,
            };
        }

        return null;
    }

    getShowCount = () => {
        const { showCount, breakpoints } = this.props;
        let newShowCount = showCount;

        if (!this.list || !Array.isArray(breakpoints) || breakpoints.length <= 0) {
            return showCount;
        }
        const containerWidth = this.list.clientWidth;
        newShowCount = breakpoints[breakpoints.length - 1].showCount;
        for (let i = 0; i < breakpoints.length; i++) {
            if (containerWidth < breakpoints[i].maxWidth) {
                newShowCount = breakpoints[i].showCount;
                break;
            }
        }

        return newShowCount;
    };

    setWidth = () => {
        if (this.list) {
            const containerWidth = this.list.clientWidth;
            const { focusItemWidthKoeff, modificator, list, fixedItemWidth } = this.props;
            const isStair = isStairMod(modificator);
            const newShowCount = this.getShowCount();
            const filteredKoeffs = stairKoeffs.slice(0, newShowCount);
            const itemWidth =
                fixedItemWidth ||
                (isStair ? containerWidth / sum(filteredKoeffs) : containerWidth / (newShowCount + focusItemWidthKoeff - 1));

            const itemWidths = [];
            for (let i = 0; i < newShowCount; i++) {
                let width = i === 1 ? focusItemWidthKoeff * itemWidth : itemWidth;
                if (isStair) {
                    width = filteredKoeffs[i % filteredKoeffs.length] * itemWidth;
                }
                itemWidths.push(width);
            }

            const { position, showCount } = this.state;
            let visibleSliders = 0;
            list.forEach((item, index) => index >= position && index < position + showCount && visibleSliders++);
            let excessSliders = newShowCount - visibleSliders;

            const callback = () => {
                if (position === 0) {
                    return;
                }

                if (excessSliders > 0) {
                    this.handleOnLeftClick();
                    return;
                }

                if (excessSliders < 0) {
                    this.handleOnRightClick();
                }
            }

            this.setState(
                {
                    itemWidths,
                    showCount: newShowCount,
                },
                callback,
            );
        }
    };

    setWidthDebounced = debounce(this.setWidth, 500);

    handleOnLeftClick = () => {
        const { position } = this.state;
        const { step } = this.props;

        const newPosition = position - step;
        const newVal = newPosition > 0 ? newPosition : 0;

        this.setState({
            position: newVal,
            selectedIdx: newVal + 1,
        });
    };

    handleOnRightClick = () => {
        const { position, showCount } = this.state;
        const { step, list } = this.props;
        const newPosition = position + step;
        const maxPosition = list.length - showCount;
        const newVal = newPosition > maxPosition ? maxPosition : newPosition;

        this.setState({
            position: newVal,
            selectedIdx: newVal + 1,
        });
    };

    handleSelect = (index) => {
        const { list } = this.props;
        const { showCount } = this.state;
        const newPosition = index - 1;
        const maxPosition = list.length - showCount;
        const newVal = newPosition > maxPosition ? maxPosition : newPosition;

        this.setState({
            position: newVal,
            selectedIdx: index,
        });
    };

    renderItem = (item, index) => {
        const { position, itemWidths, selectedIdx, showCount } = this.state;
        const lastVisible = (position >= 0 ? position : 0) + showCount;
        const isVisible = index >= position && index < lastVisible;
        const Item = this.props.renderItem(this.props.list[index], index, selectedIdx === index, isVisible, position, this.handleSelect);

        const width = itemWidths[Math.abs(index - position) % itemWidths.length];

        return React.cloneElement(Item, {
            className: classNames({
                [styles.item]: true,
                [styles.item_visible]: isVisible,
                [styles.item_selected]: selectedIdx === index,
            }),
            style: { width },
        });
    };

    render() {
        const {
            modificator,
            overflow,
            outerControls,
            animateItems,
            controlsColor,
            fixedTop,
            list,
            fixedItemWidth,
            centerControls,
        } = this.props;
        const { position, itemWidths, showCount } = this.state;

        let translate = 0;
        for (let i = 0; i < position; i++) {
            translate += itemWidths[Math.abs(i - position) % itemWidths.length];
        }

        const containerWidth = this.list ? this.list.clientWidth : 0;
        const translateShift = fixedItemWidth ? (containerWidth - fixedItemWidth * showCount) / 2 : 0; // Центруем карточку
        translate -= translateShift;

        let trackWidth = 0;
        list.forEach((item, idx) => {
            trackWidth += itemWidths[Math.abs(idx - position) % itemWidths.length];
        });

        return (
            <div
                className={classNames({
                    [styles.root]: true,
                    [styles.root_outerControls]: outerControls,
                    [styles.root_animateItems]: animateItems,
                })}
            >
                <div
                    ref={(node) => {
                        this.list = node;
                    }}
                    className={classNames({
                        [styles.list]: true,
                        [styles.list_overflow]: overflow,
                    })}
                >
                    <div
                        style={
                            trackWidth
                                ? {
                                      width: trackWidth,
                                      transform: `translate3d(${translate * -1}px, 0, 0)`,
                                  }
                                : {}
                        }
                        className={classNames({
                            [styles.track]: true,
                            [styles[`track_${modificator}`]]: !!modificator,
                        })}
                    >
                        {list.map(this.renderItem)}
                    </div>
                </div>
                <div
                    onClick={this.handleOnLeftClick}
                    className={classNames({
                        [styles.control]: true,
                        [styles[`control_${modificator}`]]: !!modificator,
                        [styles.control_left]: true,
                        [styles[`control_color_${controlsColor}`]]: controlsColor,
                        [styles.control_visible]: position > 0,
                        [styles.control_visible_fixedTop]: !!fixedTop,
                        [styles.control_center]: !!centerControls,
                    })}
                />
                <div
                    onClick={this.handleOnRightClick}
                    className={classNames({
                        [styles.control]: true,
                        [styles[`control_${modificator}`]]: !!modificator,
                        [styles.control_right]: true,
                        [styles[`control_color_${controlsColor}`]]: controlsColor,
                        [styles.control_visible]: position < list.length - showCount,
                        [styles.control_visible_fixedTop]: !!fixedTop,
                        [styles.control_center]: !!centerControls,
                    })}
                />
            </div>
        );
    }
}
